import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Users, Shield, KeyRound, UserPlus, UserX, CheckCircle2, XCircle } from 'lucide-react'
import { PageLayout } from '../layouts/PageLayout'
import { AdminNavTabs } from '../components/admin/AdminNavTabs'
import { GovernanceListingSection } from '../components/governance/GovernanceListingSection'
import { Button, TextField } from '../components/ui'
import { Select } from '../components/ui/Select'
import { useAuth } from '../components/AuthProvider'
import { useI18n } from '../i18n'
import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId, listUserOrganizations } from '../utils/orgUtils'
import {
  acceptOrgView,
  assignmentLookup,
  isPlatformAdminAccount,
  resolveSettingsOrganizationId,
} from '../utils/orgContext.mjs'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import adminStyles from '@/styles/pages/AdminPanel.module.scss'

type OrgMember = {
  id: string
  userId: string
  role: string
  customPermissions: string[]
  isActive: boolean
  assignedAt?: string | null
  user?: { id: string; username: string; email: string; fullName?: string | null }
}

type RoleDef = {
  role: string
  permissions: Record<
    string,
    { canView: boolean; canEdit: boolean; canApprove: boolean; canManage: boolean }
  >
}

type EffectivePermissions = RoleDef['permissions']

const DOMAINS = [
  'ASSETS',
  'RISKS',
  'COMPLIANCE',
  'EVIDENCE',
  'CERTIFICATION',
  'AI',
  'AI_RECOMMENDATIONS',
  'INTEGRATIONS',
  'USERS',
  'SETTINGS',
]

type CustomRole = {
  id: string
  name: string
  description?: string | null
  permissions: string[]
}

function formatApiError(status: number, body: { error?: string; code?: string; message?: string } | null): string {
  if (status === 403) {
    return body?.message || body?.error || 'You do not have permission for this action in this organization'
  }
  if (status === 409) {
    return body?.message || body?.error || 'Conflict: last OWNER cannot be removed'
  }
  return body?.error || body?.message || `Request failed (${status})`
}

export default function OrganizationSettingsWorkspace() {
  const { t } = useI18n()
  const { token, user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const isPlatformAdmin = isPlatformAdminAccount(user)
  const membershipOptions = useMemo(
    () => listUserOrganizations(),
    [user?.id, user?.organizationIds, user?.organizationsDetailed, user?.organizations],
  )
  const [catalog, setCatalog] = useState<Array<{ id: string; name: string }>>([])
  const [catalogLoaded, setCatalogLoaded] = useState(false)
  const explicitOrgId = searchParams.get('organization_id') || ''
  const organizationId = resolveSettingsOrganizationId({
    explicitOrgId,
    membershipIds: membershipOptions.map((org) => org.id),
    catalogIds: catalog.map((org) => org.id),
    activeOrgId: getActiveOrgId(),
    isPlatformAdmin,
    catalogLoaded,
  }) || ''
  const orgOptions = useMemo(() => {
    if (!isPlatformAdmin) return membershipOptions
    const options = catalog.length ? catalog : []
    if (organizationId && !options.some((org) => org.id === organizationId)) {
      return [{ id: organizationId, name: organizationId }, ...options]
    }
    return options.length ? options : membershipOptions
  }, [catalog, isPlatformAdmin, membershipOptions, organizationId])
  const organizationName = orgOptions.find((org) => org.id === organizationId)?.name || ''
  const [tab, setTab] = useState('members')
  const [members, setMembers] = useState<OrgMember[]>([])
  const [roles, setRoles] = useState<RoleDef[]>([])
  const [myPermissions, setMyPermissions] = useState<EffectivePermissions | null>(null)
  const [assignableRoles, setAssignableRoles] = useState<string[]>([])
  const [myRole, setMyRole] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('VIEWER')
  const [assignQuery, setAssignQuery] = useState('')
  const [assignRole, setAssignRole] = useState('OWNER')
  const viewRef = useRef({ generation: 0, userId: null as string | null, orgId: null as string | null })
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([])
  const [customRoleName, setCustomRoleName] = useState('')
  const [customRolePerms, setCustomRolePerms] = useState('ASSETS.VIEW\nRISKS.VIEW')
  const [busyUserId, setBusyUserId] = useState<string | null>(null)

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    [token],
  )

  const canManageUsers = Boolean(myPermissions?.USERS?.canManage)
  const canViewSettings = Boolean(myPermissions?.SETTINGS?.canView || canManageUsers)

  useEffect(() => {
    if (!isPlatformAdmin || !token) {
      setCatalog([])
      setCatalogLoaded(!isPlatformAdmin)
      return
    }
    let cancelled = false
    const loadCatalog = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.ADMIN_ORGANIZATIONS, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const body = await res.json().catch(() => null)
        const list = Array.isArray(body) ? body : body?.organizations ?? []
        if (cancelled) return
        setCatalog(
          (Array.isArray(list) ? list : [])
            .filter((row: { id?: string }) => row?.id)
            .map((row: { id: string; name?: string }) => ({
              id: String(row.id),
              name: row.name || String(row.id),
            })),
        )
      } catch {
        if (!cancelled) setCatalog([])
      } finally {
        if (!cancelled) setCatalogLoaded(true)
      }
    }
    void loadCatalog()
    return () => {
      cancelled = true
    }
  }, [isPlatformAdmin, token])

  const load = useCallback(async () => {
    if (!organizationId) {
      setLoading(false)
      setMembers([])
      setMyPermissions(null)
      setAssignableRoles([])
      setError(t('orgSettings.noOrganization', 'Select an organization to manage members'))
      return
    }
    const ticket = {
      generation: viewRef.current.generation + 1,
      userId: user?.id ? String(user.id) : null,
      orgId: organizationId,
    }
    viewRef.current = ticket
    setLoading(true)
    setError(null)
    setMembers([])
    setMyPermissions(null)
    setAssignableRoles([])
    setCustomRoles([])
    const stillCurrent = () => acceptOrgView(ticket, viewRef.current)
    try {
      const [membersRes, rolesRes, meRes, customRolesRes] = await Promise.all([
        fetch(API_ENDPOINTS.ORG_RBAC_MEMBERS(organizationId), { headers }),
        fetch(API_ENDPOINTS.ORG_RBAC_ROLES, { headers }),
        fetch(API_ENDPOINTS.ORG_RBAC_PERMISSIONS_ME(organizationId), { headers }),
        fetch(API_ENDPOINTS.ORG_RBAC_CUSTOM_ROLES(organizationId), { headers }),
      ])

      if (!stillCurrent()) return
      if (meRes.ok) {
        const body = await meRes.json()
        if (!stillCurrent()) return
        setMyPermissions(body?.permissions || null)
        setMyRole(body?.role || null)
        setAssignableRoles(Array.isArray(body?.assignableRoles) ? body.assignableRoles : [])
      } else if (meRes.status === 403) {
        setMyPermissions(null)
        setAssignableRoles([])
        setError(t('orgSettings.forbidden', 'You do not have access to this organization'))
      }

      if (membersRes.ok) {
        const body = await membersRes.json()
        if (!stillCurrent()) return
        setMembers(Array.isArray(body?.data) ? body.data : [])
      } else if (membersRes.status === 403) {
        setMembers([])
        setError(t('orgSettings.membersForbidden', 'You cannot view members for this organization'))
      } else if (!membersRes.ok) {
        const body = await membersRes.json().catch(() => null)
        setError(formatApiError(membersRes.status, body))
      }

      if (rolesRes.ok) {
        const body = await rolesRes.json()
        if (!stillCurrent()) return
        setRoles(Array.isArray(body?.data) ? body.data : [])
      }
      if (customRolesRes.ok) {
        const body = await customRolesRes.json()
        if (!stillCurrent()) return
        setCustomRoles(Array.isArray(body?.data) ? body.data : [])
      }
    } catch {
      if (stillCurrent()) setError(t('orgSettings.loadFailed', 'Unable to load organization settings'))
    } finally {
      if (stillCurrent()) setLoading(false)
    }
  }, [headers, organizationId, t, user?.id])

  useEffect(() => {
    void load()
  }, [load])

  const roleOptions = useMemo(() => {
    const allowed = assignableRoles.length
      ? assignableRoles
      : canManageUsers
        ? ['ADMIN', 'CISO', 'COMPLIANCE_MANAGER', 'RISK_MANAGER', 'ASSET_MANAGER', 'AUDITOR', 'VIEWER']
        : []
    const customOpts = customRoles
      .filter((r) => allowed.some((a) => a === `CUSTOM:${r.id}` || a.startsWith('CUSTOM:')))
      .map((r) => ({ value: `CUSTOM:${r.id}`, label: r.name }))
    // Prefer server assignable list; include custom roles only when OWNER (assignable includes CUSTOM tokens rarely — show for OWNER)
    const standard = allowed
      .filter((r) => !String(r).startsWith('CUSTOM:'))
      .map((r) => ({ value: r, label: r }))
    if (myRole === 'OWNER' || String(user?.role || '').toLowerCase() === 'admin') {
      return [...standard, ...customRoles.map((r) => ({ value: `CUSTOM:${r.id}`, label: r.name }))]
    }
    return standard.length ? standard : customOpts
  }, [assignableRoles, canManageUsers, customRoles, myRole, user?.role])

  useEffect(() => {
    if (roleOptions.length && !roleOptions.some((o) => o.value === inviteRole)) {
      setInviteRole(roleOptions[0].value)
    }
  }, [roleOptions, inviteRole])

  const inviteMember = async () => {
    if (!inviteEmail.trim() || !organizationId || !canManageUsers) return
    setBusyUserId('invite')
    setError(null)
    try {
      const res = await fetch(API_ENDPOINTS.ORG_RBAC_INVITE(organizationId), {
        method: 'POST',
        headers,
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok) {
        setError(formatApiError(res.status, body))
        return
      }
      setInviteEmail('')
      await load()
    } catch {
      setError(t('orgSettings.inviteFailed', 'Invite failed'))
    } finally {
      setBusyUserId(null)
    }
  }

  const updateMemberRole = async (userId: string, role: string) => {
    if (!organizationId || !canManageUsers) return
    setBusyUserId(userId)
    setError(null)
    try {
      const res = await fetch(API_ENDPOINTS.ORG_RBAC_MEMBER(userId, organizationId), {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ role }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok) {
        setError(formatApiError(res.status, body))
        await load()
        return
      }
      await load()
    } finally {
      setBusyUserId(null)
    }
  }

  const deactivateMember = async (userId: string) => {
    if (!organizationId || !canManageUsers) return
    setBusyUserId(userId)
    setError(null)
    try {
      const res = await fetch(API_ENDPOINTS.ORG_RBAC_DEACTIVATE(userId, organizationId), {
        method: 'POST',
        headers,
      })
      const body = await res.json().catch(() => null)
      if (!res.ok) {
        setError(formatApiError(res.status, body))
        return
      }
      await load()
    } finally {
      setBusyUserId(null)
    }
  }

  const reactivateMember = async (userId: string, role: string) => {
    if (!organizationId || !canManageUsers) return
    setBusyUserId(userId)
    setError(null)
    try {
      const res = await fetch(API_ENDPOINTS.ORG_RBAC_MEMBER(userId, organizationId), {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ role, isActive: true }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok) {
        setError(formatApiError(res.status, body))
        return
      }
      await load()
    } finally {
      setBusyUserId(null)
    }
  }

  const createCustomRole = async () => {
    if (!customRoleName.trim() || !organizationId || !canManageUsers) return
    setBusyUserId('custom-role')
    try {
      const permissions = customRolePerms
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
      const res = await fetch(API_ENDPOINTS.ORG_RBAC_CUSTOM_ROLES(organizationId), {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: customRoleName.trim(), permissions }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        setError(formatApiError(res.status, body))
        return
      }
      setCustomRoleName('')
      await load()
    } catch {
      setError(t('orgSettings.customRoleFailed', 'Failed to create custom role'))
    } finally {
      setBusyUserId(null)
    }
  }

  const tabs = [
    { key: 'members', label: t('orgSettings.tabs.members', 'Members'), icon: <Users size={16} /> },
    { key: 'roles', label: t('orgSettings.tabs.roles', 'Roles'), icon: <Shield size={16} /> },
    { key: 'permissions', label: t('orgSettings.tabs.permissions', 'Permissions'), icon: <KeyRound size={16} /> },
  ]

  const assignExistingMember = async () => {
    const lookup = assignmentLookup(assignQuery)
    if (!lookup || !organizationId || !canManageUsers) return
    setBusyUserId('assign')
    setError(null)
    try {
      const res = await fetch(API_ENDPOINTS.ORG_RBAC_ASSIGN(organizationId), {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...lookup, role: assignRole }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok) {
        setError(formatApiError(res.status, body))
        return
      }
      setAssignQuery('')
      await load()
    } catch {
      setError(t('orgSettings.assignFailed', 'Could not assign the existing user'))
    } finally {
      setBusyUserId(null)
    }
  }

  const onSelectOrganization = (nextId: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('organization_id', nextId)
    setSearchParams(next, { replace: true })
  }

  const memberRoleOptions = (member: OrgMember) => {
    if (!canManageUsers) return [{ value: member.role, label: member.role }]
    if (member.role === 'OWNER' && myRole !== 'OWNER' && String(user?.role || '').toLowerCase() !== 'admin') {
      return [{ value: 'OWNER', label: 'OWNER' }]
    }
    if (!roleOptions.some((o) => o.value === member.role)) {
      return [{ value: member.role, label: member.role }, ...roleOptions]
    }
    return roleOptions
  }

  return (
    <PageLayout
      title={
        organizationName
          ? `${t('orgSettings.title', 'Organization Settings')} — ${organizationName}`
          : t('orgSettings.title', 'Organization Settings')
      }
      subtitle={t('orgSettings.subtitle', 'Manage members, roles, and delegated access for your organization')}
    >
      <div className={listStyles.pageStack}>
        {orgOptions.length > 0 ? (
          <div className={adminStyles.formStack} style={{ maxWidth: 420 }}>
            <Select
              label={t('orgSettings.organization', 'Organization')}
              value={organizationId}
              onChange={(value) => {
                if (value != null) onSelectOrganization(String(value))
              }}
              options={orgOptions.map((o) => ({ value: o.id, label: o.name }))}
            />
          </div>
        ) : null}

        <AdminNavTabs
          items={tabs}
          activeKey={tab}
          onChange={setTab}
          ariaLabel={t('orgSettings.ariaTabs', 'Organization settings sections')}
        />

        {error ? (
          <div className={adminStyles.errorBanner} role="alert">
            {error}
          </div>
        ) : null}

        {tab === 'members' ? (
          <GovernanceListingSection
            title={t('orgSettings.membersTitle', 'Organization members')}
            description={t('orgSettings.membersDesc', 'Invite personnel and assign organization-scoped roles.')}
          >
            {canManageUsers ? (
              <div className={listStyles.metricsGrid}>
                <article className={listStyles.panel}>
                  <h3 className={listStyles.panelExternalTitle}>
                    <UserPlus size={18} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} />
                    {t('orgSettings.assignTitle', 'Assign existing user')}
                  </h3>
                  <div className={adminStyles.formStack}>
                    <TextField
                      label={t('orgSettings.existingUser', 'Email, username, or user id')}
                      value={assignQuery}
                      onChange={(value) => setAssignQuery(value)}
                      placeholder="person@company.com"
                    />
                    <Select
                      label={t('orgSettings.role', 'Role')}
                      value={assignRole}
                      onChange={(value) => {
                        if (value != null) setAssignRole(String(value))
                      }}
                      options={(isPlatformAdmin || myRole === 'OWNER'
                        ? ['OWNER', 'ADMIN']
                        : ['ADMIN']
                      ).map((role) => ({ value: role, label: role }))}
                    />
                    <Button onClick={() => void assignExistingMember()} disabled={busyUserId === 'assign'}>
                      {t('orgSettings.assign', 'Assign member')}
                    </Button>
                  </div>
                </article>
                <article className={listStyles.panel}>
                  <h3 className={listStyles.panelExternalTitle}>
                    <UserPlus size={18} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} />
                    {t('orgSettings.inviteTitle', 'Invite member')}
                  </h3>
                  <div className={adminStyles.formStack}>
                    <TextField
                      label={t('orgSettings.email', 'Email')}
                      value={inviteEmail}
                      onChange={(value) => setInviteEmail(value)}
                      placeholder="person@company.com"
                    />
                    <Select
                      label={t('orgSettings.role', 'Role')}
                      value={inviteRole}
                      onChange={(value) => {
                        if (value != null) setInviteRole(String(value))
                      }}
                      options={roleOptions}
                    />
                    <Button onClick={() => void inviteMember()} disabled={busyUserId === 'invite'}>
                      {t('orgSettings.invite', 'Send invite')}
                    </Button>
                  </div>
                </article>
              </div>
            ) : canViewSettings ? (
              <p className={listStyles.panelSubtitle}>
                {t('orgSettings.viewOnlyMembers', 'You can view members but cannot manage roles for this organization.')}
              </p>
            ) : null}

            {loading ? (
              <p className={listStyles.panelSubtitle}>{t('common.loading', 'Loading…')}</p>
            ) : members.length === 0 ? (
              <p className={listStyles.panelSubtitle}>
                {t('orgSettings.noMembers', 'This organization has no members yet.')}
              </p>
            ) : (
              <div className={listStyles.metricsGrid}>
                {members.map((member) => (
                  <article key={member.id} className={listStyles.panel}>
                    <div className={listStyles.panelHeader}>
                      <div className={listStyles.panelIntro}>
                        <h3 className={listStyles.panelTitle}>
                          {member.user?.fullName || member.user?.username || member.userId}
                        </h3>
                        <p className={listStyles.panelSubtitle}>{member.user?.email}</p>
                      </div>
                      <span
                        className={`${listStyles.badge} ${
                          member.isActive ? listStyles.badgeEvidenceValid : listStyles.badgeDefault
                        }`}
                      >
                        {member.isActive
                          ? t('orgSettings.active', 'Active')
                          : t('orgSettings.inactive', 'Inactive')}
                      </span>
                    </div>
                    <div className={adminStyles.formStack}>
                      <Select
                        label={t('orgSettings.role', 'Role')}
                        value={member.role}
                        onChange={(value) => {
                          if (value != null) void updateMemberRole(member.userId, String(value))
                        }}
                        disabled={
                          busyUserId === member.userId ||
                          !member.isActive ||
                          !canManageUsers ||
                          (member.role === 'OWNER' && myRole !== 'OWNER' && String(user?.role || '').toLowerCase() !== 'admin')
                        }
                        options={memberRoleOptions(member)}
                      />
                      {canManageUsers && member.isActive ? (
                        <Button
                          variant="secondary"
                          onClick={() => void deactivateMember(member.userId)}
                          disabled={
                            busyUserId === member.userId ||
                            (member.role === 'OWNER' && myRole !== 'OWNER' && String(user?.role || '').toLowerCase() !== 'admin')
                          }
                        >
                          <UserX size={16} />
                          {t('orgSettings.deactivate', 'Deactivate')}
                        </Button>
                      ) : null}
                      {canManageUsers && !member.isActive ? (
                        <Button
                          variant="secondary"
                          onClick={() => void reactivateMember(member.userId, member.role)}
                          disabled={busyUserId === member.userId}
                        >
                          {t('orgSettings.reactivate', 'Reactivate')}
                        </Button>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </GovernanceListingSection>
        ) : null}

        {tab === 'roles' ? (
          <GovernanceListingSection
            title={t('orgSettings.rolesTitle', 'Standard roles')}
            description={t('orgSettings.rolesDesc', 'Enterprise separation of duties across compliance, risk, assets, and AI.')}
          >
            {canManageUsers ? (
              <article className={listStyles.panel}>
                <h3 className={listStyles.panelTitle}>{t('orgSettings.createCustomRole', 'Create custom role')}</h3>
                <div className={adminStyles.formStack}>
                  <TextField
                    label={t('orgSettings.customRoleName', 'Role name')}
                    value={customRoleName}
                    onChange={(value) => setCustomRoleName(value)}
                    placeholder="Vendor Manager"
                  />
                  <TextField
                    label={t('orgSettings.customRolePerms', 'Permissions (one per line)')}
                    value={customRolePerms}
                    onChange={(value) => setCustomRolePerms(value)}
                    placeholder={'ASSETS.VIEW\nRISKS.VIEW'}
                    multiline
                  />
                  <Button onClick={() => void createCustomRole()} disabled={busyUserId === 'custom-role'}>
                    {t('orgSettings.saveCustomRole', 'Save custom role')}
                  </Button>
                </div>
              </article>
            ) : null}

            <div className={listStyles.metricsGrid}>
              {roles.map((roleDef) => (
                <article key={roleDef.role} className={listStyles.panel}>
                  <h3 className={listStyles.panelTitle}>{roleDef.role}</h3>
                  <div className={listStyles.panelToolbar} style={{ justifyContent: 'flex-start' }}>
                    {DOMAINS.filter((d) => roleDef.permissions?.[d]?.canManage).map((d) => (
                      <span key={`${roleDef.role}-${d}-m`} className={`${listStyles.badge} ${listStyles.badgeCompleted}`}>
                        {d} · manage
                      </span>
                    ))}
                    {DOMAINS.filter(
                      (d) => roleDef.permissions?.[d]?.canApprove && !roleDef.permissions?.[d]?.canManage,
                    ).map((d) => (
                      <span key={`${roleDef.role}-${d}-a`} className={`${listStyles.badge} ${listStyles.badgeInProgress}`}>
                        {d} · approve
                      </span>
                    ))}
                    {DOMAINS.filter(
                      (d) =>
                        roleDef.permissions?.[d]?.canEdit &&
                        !roleDef.permissions?.[d]?.canApprove &&
                        !roleDef.permissions?.[d]?.canManage,
                    ).map((d) => (
                      <span key={`${roleDef.role}-${d}-e`} className={`${listStyles.badge} ${listStyles.badgePlanned}`}>
                        {d} · edit
                      </span>
                    ))}
                    {DOMAINS.filter(
                      (d) =>
                        roleDef.permissions?.[d]?.canView &&
                        !roleDef.permissions?.[d]?.canEdit &&
                        !roleDef.permissions?.[d]?.canApprove &&
                        !roleDef.permissions?.[d]?.canManage,
                    ).map((d) => (
                      <span key={`${roleDef.role}-${d}-v`} className={`${listStyles.badge} ${listStyles.badgeDefault}`}>
                        {d} · view
                      </span>
                    ))}
                  </div>
                </article>
              ))}
              {customRoles.map((role) => (
                <article key={role.id} className={listStyles.panel}>
                  <h3 className={listStyles.panelTitle}>{role.name}</h3>
                  <p className={listStyles.panelSubtitle}>{t('orgSettings.customRole', 'Custom role')}</p>
                  <div className={listStyles.panelToolbar} style={{ justifyContent: 'flex-start' }}>
                    {role.permissions.map((perm) => (
                      <span key={perm} className={`${listStyles.badge} ${listStyles.badgePlanned}`}>
                        {perm}
                      </span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </GovernanceListingSection>
        ) : null}

        {tab === 'permissions' ? (
          <GovernanceListingSection
            title={t('orgSettings.permissionsTitle', 'Your effective permissions')}
            description={t('orgSettings.permissionsDesc', 'Resolved from your organization role, custom grants, and legacy access.')}
          >
            <div className={listStyles.metricsGrid}>
              {DOMAINS.map((domain) => {
                const p = myPermissions?.[domain]
                return (
                  <article key={domain} className={listStyles.panel}>
                    <h3 className={listStyles.panelTitle}>{domain}</h3>
                    <ul className={adminStyles.permissionList}>
                      <li className={adminStyles.permissionRow}>
                        {p?.canView ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                        <span>{t('orgSettings.canView', 'View')}</span>
                      </li>
                      <li className={adminStyles.permissionRow}>
                        {p?.canEdit ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                        <span>{t('orgSettings.canEdit', 'Edit')}</span>
                      </li>
                      <li className={adminStyles.permissionRow}>
                        {p?.canApprove ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                        <span>{t('orgSettings.canApprove', 'Approve')}</span>
                      </li>
                      <li className={adminStyles.permissionRow}>
                        {p?.canManage ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                        <span>{t('orgSettings.canManage', 'Manage')}</span>
                      </li>
                    </ul>
                  </article>
                )
              })}
            </div>
          </GovernanceListingSection>
        ) : null}
      </div>
    </PageLayout>
  )
}
